import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Globe, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { deletePage, listPages, publishPage, unpublishPage, type Page } from '../../lib/content-api'
import { statusBadgeVariant } from './fields'

export function PagesAdmin() {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['content', 'pages'],
    queryFn: listPages,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['content', 'pages'] })

  const publishMut = useMutation({
    mutationFn: (p: Page) => (p.status === 'published' ? unpublishPage(p.id) : publishPage(p.id)),
    onSuccess: invalidate,
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePage(id),
    onSuccess: invalidate,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pages"
        description="Public website pages with a draft / publish workflow."
        actions={
          <Button asChild>
            <Link to="/app/content/pages/new">
              <Plus />
              New page
            </Link>
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading pages…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load pages.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <FileText className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              No pages yet. Create your first page to publish content on your church site.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Title</th>
                  <th className="px-5 py-3 font-semibold">Slug</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/app/content/pages/${p.id}`}
                        className="font-medium hover:text-secondary"
                      >
                        {p.title}
                      </Link>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-muted">/{p.slug}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusBadgeVariant(p.status)} className="capitalize">
                        {p.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => publishMut.mutate(p)}
                          disabled={publishMut.isPending}
                          title={p.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                          <Globe />
                          {p.status === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button variant="ghost" size="icon-sm" asChild aria-label="Edit page">
                          <Link to={`/app/content/pages/${p.id}`}>
                            <Pencil />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Delete page"
                          onClick={() => {
                            if (confirm(`Delete “${p.title}”? This cannot be undone.`))
                              deleteMut.mutate(p.id)
                          }}
                          disabled={deleteMut.isPending}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
